'use client';

import { useEffect, useRef, useState } from 'react';
import type { AccountDetailsResponse } from '@/services/microservices/account';
import { fetchAccountSummary } from '@/services/microservices/accountSummary';
import { getMicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import { CARE_ACCOUNT_URLS, buildRefreshRadioUrl } from './careAccountUrls';
import { SnapshotAuthState } from './SnapshotUser.types';
import { getClientSdk } from './clientSdk';

export type SubscriptionMenuVariant = 'satellite' | 'streaming';

export interface SnapshotSubscriptionState {
    variant: SubscriptionMenuVariant;
    refreshRadioHref: string;
    transferSubscriptionHref: string;
}

interface SnapshotNonPiiSubscription {
    status?: string;
    radioService?: {
        id?: string;
        last4DigitsOfRadioId?: string;
        radioId?: string;
    };
    streamingService?: unknown;
}

interface SnapshotAccountSummaryResponseShape {
    data?: {
        subscriptions?: SnapshotNonPiiSubscription[];
        nonPIIAccount?: {
            subscriptions?: SnapshotNonPiiSubscription[];
        };
    };
}

interface SnapshotAccountDetailsResponseShape {
    data?: {
        subscriptions?: SnapshotNonPiiSubscription[];
        streamingOnly?: boolean;
    };
}

type ClientSdk = NonNullable<BrowserWindow['clientSdk']>;
type ClientSubscriptionsResponse = Awaited<ReturnType<ClientSdk['commerce']['subscription']['getSubscriptions']>>;
type ClientSubscriptionItem = NonNullable<ClientSubscriptionsResponse['items']>[number];

const isActiveStatus = (status?: string): boolean => {
    if (!status) return false;
    const normalized = status.toUpperCase();
    return normalized === 'ACTIVE' || normalized === 'IN_GRACE' || normalized === 'PAST_DUE';
};

const isExpiredClientSubscriptionState = (state?: string | null): boolean => {
    const normalized = state?.trim().toLowerCase();

    return normalized === 'expired' || normalized === 'cancelled';
};

const hasAutomotiveSource = (subscription: ClientSubscriptionItem): boolean =>
    Boolean(subscription.source && 'automotive' in subscription.source);

const hasDeviceBeneficiary = (subscription: ClientSubscriptionItem): boolean =>
    Boolean(subscription.beneficiaries?.some((beneficiary) => beneficiary.type === 'device'));

const hasSatelliteSubscriptionFallback = (subscriptionsResponse: ClientSubscriptionsResponse | undefined): boolean =>
    Boolean(
        subscriptionsResponse?.items?.some(
            (subscription) =>
                !isExpiredClientSubscriptionState(subscription.state) &&
                (hasAutomotiveSource(subscription) || hasDeviceBeneficiary(subscription)),
        ),
    );

const getAccountSummarySubscriptions = (response: SnapshotAccountSummaryResponseShape): SnapshotNonPiiSubscription[] =>
    response.data?.subscriptions ?? response.data?.nonPIIAccount?.subscriptions ?? [];

const getAccountDetailsSubscriptions = (response: SnapshotAccountDetailsResponseShape): SnapshotNonPiiSubscription[] =>
    response.data?.subscriptions ?? [];

const isStreamingOnlyMenuUser = (response?: SnapshotAccountDetailsResponseShape): boolean => response?.data?.streamingOnly === true;

export const useSnapshotSubscription = (
    authState: SnapshotAuthState,
    accountNumber: string | null,
    accountDetailsResponse?: AccountDetailsResponse,
): SnapshotSubscriptionState | null => {
    const [state, setState] = useState<SnapshotSubscriptionState | null>(null);
    const loadGenerationRef = useRef(0);

    useEffect(() => {
        const generation = ++loadGenerationRef.current;
        const isStale = () => generation !== loadGenerationRef.current;

        const load = async () => {
            if (authState !== 'authenticated' || !accountNumber) {
                setState(null);
                return;
            }

            const clientSdk = await getClientSdk();

            if (!clientSdk || isStale()) {
                return;
            }

            const userInfo = clientSdk.user.getUserInfo();

            if (userInfo.session?.sessionType !== 'authenticated') {
                setState(null);
                return;
            }

            try {
                const countryCode = getMicroservicesCountryCode();
                const response =
                    accountDetailsResponse ??
                    (await fetchAccountSummary(countryCode, {
                        accountNumber,
                        retrieveAllSubscriptions: 'ALL',
                    }));

                if (isStale()) {
                    return;
                }

                const subscriptions =
                    accountDetailsResponse
                        ? getAccountDetailsSubscriptions(accountDetailsResponse)
                        : getAccountSummarySubscriptions(response);
                const activeSubscriptions = subscriptions.filter((sub) => isActiveStatus(sub.status));
                const activeSatellites = activeSubscriptions.filter((sub) => !!sub.radioService);
                const firstActiveSatellite = activeSatellites[0];
                const radioId = firstActiveSatellite?.radioService?.radioId;
                const shouldShowSatelliteActionsForStreamingOnlyUser = isStreamingOnlyMenuUser(accountDetailsResponse);

                let hasSatelliteSubscription =
                    activeSatellites.length > 0 || shouldShowSatelliteActionsForStreamingOnlyUser;

                if (!hasSatelliteSubscription) {
                    const subscriptionsResponse = (await clientSdk.commerce.subscription
                        .getSubscriptions()
                        .catch(() => undefined)) as ClientSubscriptionsResponse | undefined;

                    if (isStale()) {
                        return;
                    }

                    hasSatelliteSubscription = hasSatelliteSubscriptionFallback(subscriptionsResponse);
                }

                const variant: SubscriptionMenuVariant =
                    hasSatelliteSubscription ? 'satellite' : 'streaming';

                setState({
                    variant,
                    refreshRadioHref: buildRefreshRadioUrl(radioId),
                    transferSubscriptionHref: CARE_ACCOUNT_URLS.transferSubscription,
                });
            } catch (error) {
                console.warn('[snapshot-subscription] Failed to load account summary', error);

                const subscriptionsResponse = (await clientSdk.commerce.subscription
                    .getSubscriptions()
                    .catch(() => undefined)) as ClientSubscriptionsResponse | undefined;

                if (isStale()) {
                    return;
                }

                setState({
                    variant: hasSatelliteSubscriptionFallback(subscriptionsResponse) ? 'satellite' : 'streaming',
                    refreshRadioHref: buildRefreshRadioUrl(undefined),
                    transferSubscriptionHref: CARE_ACCOUNT_URLS.transferSubscription,
                });
            }
        };

        void load();

        return () => {
            loadGenerationRef.current += 1;
        };
    }, [accountDetailsResponse, accountNumber, authState]);

    return state;
};
